// Pure debrief analysis: the sync matrix, the lost-time list, COP accuracy, and the decision log. All
// four functions are pure over the committed event log / order list / unit list - no sim state, no I/O -
// so the debrief UI (Task 19) and its tests can call them directly. See spec §2 Stage 4 "Debrief" and
// plan Task 19.
import { card } from '../sim/deck';
import type { Grid } from '../sim/grid';
import { TICKS_PER_MIN } from '../sim/types';
import type { CardId, Event, Graphic, Order, Scenario, Unit } from '../sim/types';
import type { TruthFrame } from '../sim/sim';

const REACT_SLOW_TICKS = 5 * TICKS_PER_MIN; // 50 ticks / 5 min
const REACT_TIMEOUT_TICKS = 15 * TICKS_PER_MIN; // 150 ticks / 15 min
const FUEL_GRACE_TICKS = 10 * TICKS_PER_MIN; // 100 ticks / 10 min
const DECISION_WINDOW_TICKS = 5 * TICKS_PER_MIN; // 50 ticks / 5 min

const ISR_CARDS = new Set(['launchUas', 'recon', 'observe']);
const RESUPPLY_CARDS = new Set(['resupply', 'withdraw']);

const PHASE_NAMES = ['Before PL RED', 'PL RED–WHITE', 'PL WHITE–BLUE', 'Beyond PL BLUE'] as const;

// ---- buildSyncMatrix --------------------------------------------------------------------------------

/** The tick of the first blue `pl.crossed` event whose payload name mentions `token` (RED/WHITE/BLUE), or undefined. */
function firstBlueCrossing(events: Event[], token: 'RED' | 'WHITE' | 'BLUE'): number | undefined {
  let best: number | undefined;
  for (const ev of events) {
    if (ev.kind !== 'pl.crossed' || ev.side !== 0) continue;
    const name = String(ev.payload.name ?? '');
    if (!name.toUpperCase().includes(token)) continue;
    if (best === undefined || ev.t < best) best = ev.t;
  }
  return best;
}

function phaseIndexOf(t: number, boundaries: [number, number, number]): number {
  if (t < boundaries[0]) return 0;
  if (t < boundaries[1]) return 1;
  if (t < boundaries[2]) return 2;
  return 3;
}

/** Short label for a key (non-order) event kind this unit was the subject of, or null if not one we track. */
function keyEventLabel(ev: Event): string | null {
  switch (ev.kind) {
    case 'unit.pinned': return 'pinned';
    case 'unit.broken': return 'BROKEN';
    case 'unit.destroyed': return 'DESTROYED';
    case 'unit.fuelLow': return 'fuel low';
    case 'pl.crossed': return `crossed ${String(ev.payload.name ?? '')}`.trim();
    default: return null;
  }
}

export function buildSyncMatrix(
  events: Event[],
  units: Unit[],
  graphics: Graphic[],
): { phases: string[]; rows: { unitId: number; callsign: string; cells: string[] }[] } {
  void graphics; // PL names come straight off pl.crossed payloads; graphics kept for interface parity.

  const tRed = firstBlueCrossing(events, 'RED');
  const tWhite = firstBlueCrossing(events, 'WHITE');
  const tBlue = firstBlueCrossing(events, 'BLUE');

  // Missing boundaries collapse forward onto the next one that exists (or +Infinity), so a skipped PL
  // produces an empty phase rather than misplacing later activity.
  const b2 = tBlue ?? Infinity;
  const b1 = tWhite ?? b2;
  const b0 = tRed ?? b1;
  const boundaries: [number, number, number] = [b0, b1, b2];

  const blueUnits = units.filter((u) => u.side === 0).sort((a, b) => a.id - b.id);
  const sorted = [...events].sort((a, b) => a.id - b.id);

  const rows = blueUnits.map((u) => {
    const cells: string[] = ['', '', '', ''];
    const perPhase: string[][] = [[], [], [], []];

    for (const ev of sorted) {
      if (ev.unitId !== u.id) continue;
      const phase = phaseIndexOf(ev.t, boundaries);
      if (ev.kind === 'order.exec') {
        const cardId = ev.payload.card;
        if (typeof cardId === 'string') {
          perPhase[phase].push(card(cardId as CardId).label.toUpperCase());
        }
        continue;
      }
      const label = keyEventLabel(ev);
      if (label) perPhase[phase].push(label);
    }

    for (let i = 0; i < 4; i++) {
      const deduped: string[] = [];
      for (const s of perPhase[i]) {
        if (deduped[deduped.length - 1] !== s) deduped.push(s);
      }
      cells[i] = deduped.join(', ');
    }

    return { unitId: u.id, callsign: u.callsign, cells };
  });

  return { phases: [...PHASE_NAMES], rows };
}

// ---- lostTime -----------------------------------------------------------------------------------------

export function lostTime(
  events: Event[],
  orders: Order[],
  graphics: Graphic[],
  units: Unit[],
  /** Ticks the run lasted. Only the "never made contact" rule needs it, so it is optional: without it
   * the run length is taken to be the last event in the log. */
  runTicks?: number,
): { item: string; minutes: number; eventIds: number[] }[] {
  void graphics; // NAI names come off nai.contact payloads directly.

  const out: { item: string; minutes: number; eventIds: number[] }[] = [];
  const blueOrders = [...orders].filter((o) => o.side === 0).sort((a, b) => a.issuedAt - b.issuedAt);

  // Rule 1: slow reaction to a significant contact in an NAI.
  const naiContacts = events.filter((ev) => ev.kind === 'nai.contact' && ev.side === 0).sort((a, b) => a.t - b.t);
  for (const ev of naiContacts) {
    const next = blueOrders.find((o) => o.issuedAt > ev.t);
    if (!next) {
      out.push({
        item: `Slow reaction to contact at ${String(ev.payload.name ?? 'NAI')}`,
        minutes: 15,
        eventIds: [ev.id],
      });
      continue;
    }
    const gapTicks = next.issuedAt - ev.t;
    if (gapTicks > REACT_SLOW_TICKS) {
      out.push({
        item: `Slow reaction to contact at ${String(ev.payload.name ?? 'NAI')}`,
        minutes: gapTicks / TICKS_PER_MIN,
        eventIds: [ev.id, next.id],
      });
    }
  }

  // Rule 2: reserve (armor) never committed after crossing PL WHITE, and the objective was not seized.
  const tWhite = firstBlueCrossing(events, 'WHITE');
  const seized = events.some((ev) => ev.kind === 'obj.seized');
  if (tWhite !== undefined && !seized) {
    const armorIds = new Set(units.filter((u) => u.side === 0 && u.type === 'armor').map((u) => u.id));
    const committed = blueOrders.some(
      (o) => armorIds.has(o.unitId) && o.issuedAt > tWhite && (o.playId !== undefined || o.card === 'attack' || o.card === 'move'),
    );
    if (armorIds.size > 0 && !committed) {
      const whiteEv = events.find((ev) => ev.kind === 'pl.crossed' && ev.side === 0 && ev.t === tWhite);
      out.push({ item: 'Reserve never committed', minutes: 0, eventIds: whiteEv ? [whiteEv.id] : [] });
    }
  }

  // Rule 3: no ISR order anywhere before the first contact.
  const firstContact = events
    .filter((ev) => ev.kind === 'contact.new' && ev.side === 0)
    .sort((a, b) => a.t - b.t)[0];
  if (firstContact) {
    const isrBefore = blueOrders.some((o) => ISR_CARDS.has(o.card) && o.issuedAt < firstContact.t);
    if (!isrBefore) {
      out.push({
        item: 'No ISR on any NAI before contact',
        minutes: firstContact.t / TICKS_PER_MIN,
        eventIds: [firstContact.id],
      });
    }
  }

  // Rule 4: fuel warning with no resupply/withdraw in the following 10 minutes.
  const fuelWarnings = events.filter((ev) => ev.kind === 'force.fuelLow' && ev.side === 0);
  for (const ev of fuelWarnings) {
    const responded = blueOrders.some(
      (o) => RESUPPLY_CARDS.has(o.card) && o.issuedAt > ev.t && o.issuedAt <= ev.t + FUEL_GRACE_TICKS,
    );
    if (!responded) {
      out.push({ item: 'Fuel warning ignored', minutes: 10, eventIds: [ev.id] });
    }
  }

  // Rule 5: command net saturation episodes.
  const saturations = events.filter((ev) => ev.kind === 'net.saturated' && ev.side === 0);
  if (saturations.length > 0) {
    out.push({
      item: `Command net saturated ${saturations.length} times`,
      minutes: saturations.length * 2,
      eventIds: saturations.map((ev) => ev.id),
    });
  }

  // Rule 6: the force never found the enemy at all. Without this the panel reported "nothing lost" on a
  // run where nothing happened - the single most expensive outcome there is, scored as a clean sheet.
  const sawSomething = events.some(
    (ev) => ev.side === 0 && (ev.kind === 'contact.new' || ev.kind === 'nai.contact'),
  );
  if (!sawSomething) {
    const lastTick = events.reduce((max, ev) => Math.max(max, ev.t), 0);
    const ticks = runTicks ?? lastTick;
    const minutes = Math.max(0, ticks / TICKS_PER_MIN);
    out.push({
      item: `No contact in ${Math.round(minutes)} minutes: the force never closed with the enemy`,
      minutes,
      eventIds: [],
    });
  }

  return out.sort((a, b) => b.minutes - a.minutes);
}

// ---- copAccuracy --------------------------------------------------------------------------------------

export interface CopAccuracy {
  contactsCorrectType: number;
  /**
   * Mean metres between where a contact was reported and where the enemy actually was. `null`, never 0,
   * when no contact was ever reported: "0 m" reads as a perfect picture, and a run with no contacts in
   * it has no picture at all.
   */
  meanPositionErrorM: number | null;
  /** Fraction of reported contacts called as the right kind of unit, or `null` when there were none. */
  typeRate: number | null;
  enemiesNeverSeen: number;
}

export function copAccuracy(
  truth: TruthFrame[],
  contactEvents: Event[],
  units: Unit[],
  grid: Grid,
): CopAccuracy {
  const framesByTick = [...truth].sort((a, b) => a.tick - b.tick);
  const unitById = new Map(units.map((u) => [u.id, u]));

  let contactsCorrectType = 0;
  let errorSum = 0;
  let errorCount = 0;
  const seenEnemyIds = new Set<number>();

  for (const ev of contactEvents) {
    // detection.ts's contact.new payload is `{ enemyId, type, observerId }` - the reported type, not a
    // separate confidence field, and no position (the target cell is on the event itself, not the payload).
    const enemyId = Number(ev.payload.enemyId);
    if (Number.isFinite(enemyId)) seenEnemyIds.add(enemyId);

    const real = unitById.get(enemyId);
    const reportedType = ev.payload.typeConf;
    if (real && reportedType === real.type) contactsCorrectType++;

    if (ev.cell === undefined) continue;
    const reportedPos = grid.centerCm(ev.cell);

    let frame: TruthFrame | undefined;
    for (const f of framesByTick) {
      if (f.tick > ev.t) break;
      frame = f;
    }
    if (!frame) continue;
    const truthUnit = frame.units.find((u) => u.id === enemyId);
    if (!truthUnit) continue;

    errorSum += grid.distCm(reportedPos, { x: truthUnit.x, y: truthUnit.y }) / 100;
    errorCount++;
  }

  const enemiesNeverSeen = units.filter((u) => u.side === 1 && !seenEnemyIds.has(u.id)).length;

  return {
    contactsCorrectType,
    meanPositionErrorM: errorCount > 0 ? errorSum / errorCount : null,
    typeRate: contactEvents.length > 0 ? contactsCorrectType / contactEvents.length : null,
    enemiesNeverSeen,
  };
}

// ---- decisionLog --------------------------------------------------------------------------------------

export interface DecisionEntry {
  event: Event;
  blueOrders: Order[];
  redDecisions: Event[];
  /**
   * The other units that crossed the same phase line in the same window, when this row is a phase-line
   * crossing. Twelve platoons crossing PL RED is one event in the fight, not twelve, and twelve rows of
   * "nothing ordered" read as twelve separate failures to act.
   */
  alsoCrossed?: number[];
}

/**
 * Each significant event of *the player's own side*, with what the player ordered in the five minutes
 * that followed and what red decided in the same window.
 *
 * The side filter is the point. The log's framing is "your orders", so a red-side event in the left
 * column - an enemy platoon breaking, an enemy observer reporting - read as the player standing idle,
 * and the rapporteur duly wrote that up as inaction. Red's side of the fight has its own column.
 */
export function decisionLog(events: Event[], orders: Order[]): DecisionEntry[] {
  const significant = events
    .filter((ev) => ev.significant && ev.side === 0)
    .sort((a, b) => a.t - b.t || a.id - b.id);
  // Red's column: its reasoning, plus anything significant that happened to it in the window.
  const redEvents = events
    .filter((ev) => ev.side === 1 && (ev.kind === 'red.decision' || ev.significant))
    .sort((a, b) => a.t - b.t || a.id - b.id);

  const out: DecisionEntry[] = [];
  const foldedInto = new Map<string, DecisionEntry>();

  for (const ev of significant) {
    const windowEnd = ev.t + DECISION_WINDOW_TICKS;

    // One row per phase line: later crossings of the same line fold into the first one's row.
    if (ev.kind === 'pl.crossed') {
      const key = String(ev.payload.graphicId ?? ev.payload.name ?? '');
      const head = foldedInto.get(key);
      if (head) {
        if (ev.unitId !== undefined) (head.alsoCrossed ??= []).push(ev.unitId);
        continue;
      }
    }

    const entry: DecisionEntry = {
      event: ev,
      blueOrders: orders
        .filter((o) => o.side === 0 && o.issuedAt >= ev.t && o.issuedAt <= windowEnd)
        .sort((a, b) => a.issuedAt - b.issuedAt),
      redDecisions: redEvents.filter((r) => r.t >= ev.t && r.t <= windowEnd),
    };
    out.push(entry);
    if (ev.kind === 'pl.crossed') {
      foldedInto.set(String(ev.payload.graphicId ?? ev.payload.name ?? ''), entry);
    }
  }

  return out;
}

// ---- decisionPoints ---------------------------------------------------------------------------------
//
// The Decision Support Template and its matrix (research 03 §1): a decision point is the place and the
// moment "the commander must decide to initiate an activity to keep execution synchronized", and the
// Decision Support Matrix (DSM) is the written form of it — decision point, location, criteria to
// evaluate, options, and the unit responsible to observe and report.
//
// A staff builds those during COA analysis, before the fight, as predictions. This builds them
// afterwards, from the log, as fact: every one of them is a moment the run itself produced, with the
// criteria taken from what was actually in the player's common operating picture at that tick (never
// from ground truth — a decision point the player could not have seen is not a decision point), the
// orders that followed inside five minutes as "what you chose", and the state fifteen minutes later as
// what it cost. Pure over the log, like everything else in this file, so `tests/debrief/decisionPoints
// .test.ts` builds its events by hand and the branch replay (src/sim/branchRun.ts) can re-run from any
// of them.

/** How long after a decision point the outcome is read. */
const OUTCOME_WINDOW_TICKS = 15 * TICKS_PER_MIN;
/** How far back the criteria look for the reports that were on the table. */
const CRITERIA_LOOKBACK_TICKS = 10 * TICKS_PER_MIN;
/** A long run trips more of these than a matrix can usefully hold; the earliest are the ones that mattered. */
const MAX_DECISION_POINTS = 12;

export type DecisionPointKind = 'nai' | 'pl' | 'fuel' | 'loss' | 'redReserve' | 'objective';

/** One course of action that was legal at the decision point. `play` options were actually on the
 * table as an advisor play (they carry a `playId` that reached the order log); `template` options are
 * the three a commander always has at a decision point — commit, shift fires, or hold. */
export interface DecisionOption {
  id: 'commit-reserve' | 'shift-fires' | 'hold' | (string & {});
  label: string;
  detail: string;
  source: 'template' | 'play';
  playId?: string;
}

export interface DecisionOutcome {
  /** One line of plain English: losses and progress in the fifteen minutes that followed. */
  summary: string;
  blueDestroyed: number;
  blueBroken: number;
  redDestroyed: number;
  /** The furthest phase line blue's lead element had crossed by the end of the window. */
  plProgress: string;
  objectiveSeized: boolean;
}

export interface DecisionPoint {
  /** Stable across re-computation of the same log: `dp-<eventId>`. */
  id: string;
  /** 1-based number, in time order — the number on the template graphic and in the matrix. */
  n: number;
  t: number;
  kind: DecisionPointKind;
  /** The event that raised it, so the replay can scrub to it and the narrative can cite it. */
  eventId: number;
  /** Grid cell for the marker on the Decision Support Template, when the event had one. */
  cell?: number;
  /** Control graphic the decision hangs off, when the event named one (an NAI, a phase line). */
  graphicId?: string;
  /** "DP 3 — Enemy reserve committed north". */
  title: string;
  /** The DSM's "location" column, in words. */
  locationLabel: string;
  /** What was observable in the common operating picture at that tick: contacts held, reports in. */
  criteria: string[];
  options: DecisionOption[];
  /** The orders blue issued in the five minutes that followed, and a one-line summary of them. */
  chosen: { orders: Order[]; summary: string };
  /** The friendly unit that reported it — the DSM's "who observes" column. */
  observer: { unitId: number; callsign: string } | null;
  outcome: DecisionOutcome;
}

/** One reported contact and the window it stood in blue's picture (the analysis-side twin of the
 * replay's `contactSpans`, kept here so this module stays free of any UI import). */
interface Span { enemyId: number; typeConf: string; errorM: number; cell?: number; from: number; until: number }

function blueContactSpans(events: Event[]): Span[] {
  const spans: Span[] = [];
  const open = new Map<number, Span>();
  for (const ev of [...events].sort((a, b) => a.t - b.t || a.id - b.id)) {
    if (ev.side !== 0) continue;
    const enemyId = typeof ev.payload.enemyId === 'number' ? ev.payload.enemyId : undefined;
    if (enemyId === undefined) continue;
    if (ev.kind === 'contact.new') {
      const span: Span = {
        enemyId,
        typeConf: typeof ev.payload.typeConf === 'string' ? ev.payload.typeConf : 'unknown',
        errorM: typeof ev.payload.errorM === 'number' ? ev.payload.errorM : 0,
        cell: ev.cell,
        from: ev.t,
        until: Infinity,
      };
      spans.push(span);
      open.set(enemyId, span);
    } else if (ev.kind === 'contact.lost') {
      const span = open.get(enemyId);
      if (span) { span.until = ev.t; open.delete(enemyId); }
    }
  }
  return spans;
}

/** "2 armor, 1 vehicle, 1 unidentified" from the reported types of the contacts held at a tick. */
function contactBreakdown(spans: Span[], t: number): { count: number; text: string } {
  const held = spans.filter((s) => s.from <= t && s.until > t);
  const byType = new Map<string, number>();
  for (const s of held) {
    const label = s.typeConf === 'unknown' ? 'unidentified' : s.typeConf;
    byType.set(label, (byType.get(label) ?? 0) + 1);
  }
  const text = [...byType.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([label, n]) => `${n} ${label}`)
    .join(', ');
  return { count: held.length, text };
}

const PL_ORDER = ['pl-red', 'pl-white', 'pl-blue'];

/** The furthest phase line any blue unit had crossed by `t`, as a name. */
function furthestPl(events: Event[], t: number): string {
  let best = -2;
  let bestName = '';
  for (const ev of events) {
    if (ev.kind !== 'pl.crossed' || ev.side !== 0 || ev.t > t) continue;
    const id = String(ev.payload.graphicId ?? '');
    // -1 for a phase line the player drew themselves, which still counts as progress past it.
    const rank = PL_ORDER.indexOf(id);
    if (rank >= best) { best = rank; bestName = String(ev.payload.name ?? id); }
  }
  return bestName === '' ? 'short of PL RED' : `past ${bestName}`;
}

/** A red reserve/forward decision names the third it was committed in inside its reason string. */
function thirdOf(reason: string): string | null {
  const m = /in the (\w+) third/i.exec(reason);
  return m ? m[1].toLowerCase() : null;
}

interface Candidate {
  t: number;
  eventId: number;
  kind: DecisionPointKind;
  cell?: number;
  graphicId?: string;
  /** The title without its "DP n — " prefix. */
  headline: string;
  locationLabel: string;
  observerId?: number;
  extraCriteria: string[];
}

/**
 * Every decision point the run produced, numbered in time order.
 *
 * Detected: the first contact in each named area of interest, the first blue crossing of each phase
 * line, the task-force fuel warning, the first friendly platoon lost or broken, the enemy committing
 * its reserve (or, under the 'forward' posture, its whole force), and the objective falling.
 */
export function decisionPoints(
  events: Event[],
  orders: Order[],
  graphics: Graphic[],
  units: Unit[],
  scenario: Scenario | null,
): DecisionPoint[] {
  const allGraphics = graphics.length > 0 ? graphics : (scenario?.graphics ?? []);
  const unitById = new Map(units.map((u) => [u.id, u]));
  const sorted = [...events].sort((a, b) => a.t - b.t || a.id - b.id);
  const spans = blueContactSpans(sorted);
  const axis = allGraphics.find((g) => g.kind === 'axis');
  const objective = allGraphics.find((g) => g.id === scenario?.objectiveId) ?? allGraphics.find((g) => g.kind === 'obj');

  /** The observer who reported a given enemy at a given tick, from the `contact.new` beside it. */
  const observerFor = (t: number, enemyId: number | undefined): number | undefined => {
    if (enemyId === undefined) return undefined;
    let best: number | undefined;
    for (const ev of sorted) {
      if (ev.t > t) break;
      if (ev.kind !== 'contact.new' || ev.side !== 0 || ev.unitId !== enemyId) continue;
      const obs = ev.payload.observerId;
      if (typeof obs === 'number') best = obs;
    }
    return best;
  };

  const candidates: Candidate[] = [];
  const seenNai = new Set<string>();
  const seenPl = new Set<string>();
  let seenFuel = false;
  let seenLoss = false;
  let seenRedCommit = false;
  let seenObjective = false;

  for (const ev of sorted) {
    if (ev.kind === 'nai.contact' && ev.side === 0) {
      const key = String(ev.payload.graphicId ?? ev.payload.name ?? ev.id);
      if (seenNai.has(key)) continue;
      seenNai.add(key);
      const name = String(ev.payload.name ?? 'a named area of interest');
      candidates.push({
        t: ev.t, eventId: ev.id, kind: 'nai', cell: ev.cell,
        graphicId: typeof ev.payload.graphicId === 'string' ? ev.payload.graphicId : undefined,
        headline: `First contact in ${name}`,
        locationLabel: name,
        observerId: observerFor(ev.t, ev.unitId),
        extraCriteria: [`${name} was tripped — the information requirement it was drawn for is answered.`],
      });
      continue;
    }

    if (ev.kind === 'pl.crossed' && ev.side === 0) {
      const key = String(ev.payload.graphicId ?? ev.payload.name ?? ev.id);
      if (seenPl.has(key)) continue;
      seenPl.add(key);
      const name = String(ev.payload.name ?? 'a phase line');
      const cs = ev.unitId !== undefined ? unitById.get(ev.unitId)?.callsign ?? `UNIT ${ev.unitId}` : 'The lead element';
      candidates.push({
        t: ev.t, eventId: ev.id, kind: 'pl', cell: ev.cell,
        graphicId: typeof ev.payload.graphicId === 'string' ? ev.payload.graphicId : undefined,
        headline: `Lead element crossed ${name}`,
        locationLabel: name,
        observerId: ev.unitId,
        extraCriteria: [`${cs} is the first across ${name}; the phase it opens is now the one you are fighting.`],
      });
      continue;
    }

    if (ev.kind === 'force.fuelLow' && ev.side === 0 && !seenFuel) {
      seenFuel = true;
      const pct = typeof ev.payload.avgFuel === 'number' ? Math.round(ev.payload.avgFuel) : null;
      candidates.push({
        t: ev.t, eventId: ev.id, kind: 'fuel', cell: ev.cell,
        graphicId: axis?.id,
        headline: 'Task force fuel warning',
        locationLabel: 'the task force (no fixed location)',
        extraCriteria: [
          pct === null
            ? 'Average fuel across the force has reached the culmination warning.'
            : `Average fuel across the force is ${pct}% — the warning the sustainment staff raises before culmination.`,
        ],
      });
      continue;
    }

    if ((ev.kind === 'unit.broken' || ev.kind === 'unit.destroyed') && ev.side === 0 && !seenLoss) {
      seenLoss = true;
      const cs = ev.unitId !== undefined ? unitById.get(ev.unitId)?.callsign ?? `UNIT ${ev.unitId}` : 'A platoon';
      const verb = ev.kind === 'unit.destroyed' ? 'destroyed' : 'broke and is withdrawing';
      candidates.push({
        t: ev.t, eventId: ev.id, kind: 'loss', cell: ev.cell,
        headline: `${cs} ${verb}`,
        locationLabel: cs,
        observerId: ev.unitId,
        extraCriteria: [`${cs} is combat-ineffective; whatever it was holding or leading is now uncovered.`],
      });
      continue;
    }

    if (ev.kind === 'red.decision' && ev.side === 1 && !seenRedCommit) {
      const playId = String(ev.payload.playId ?? '');
      if (!playId.startsWith('red:reserve') && !playId.startsWith('red:forward')) continue;
      seenRedCommit = true;
      const reason = String(ev.payload.reason ?? '');
      const third = thirdOf(reason);
      const whole = playId.startsWith('red:forward');
      const where = third ? ` ${third}` : '';
      // The reserve fires on contacts crossing PL WHITE, the forward counterattack on PL RED; the
      // phase line the enemy was watching is the decision point's location, because it is the one
      // piece of ground the player could have been looking at when it happened.
      const watched = allGraphics.find((g) => g.kind === 'pl' && g.id === (whole ? 'pl-red' : 'pl-white'));
      candidates.push({
        t: ev.t, eventId: ev.id, kind: 'redReserve', cell: ev.cell,
        graphicId: watched?.id ?? axis?.id,
        headline: whole ? `Enemy committed its whole force${where}` : `Enemy reserve committed${where}`,
        locationLabel: watched ? `${watched.name}${third ? `, ${third} third` : ''}` : `the${where || ' enemy'} sector`,
        extraCriteria: [
          reason
            ? `Revealed only in this debrief — the enemy's own reason: "${reason}".`
            : 'The enemy commander committed his reserve.',
        ],
      });
      continue;
    }

    if (ev.kind === 'obj.seized' && !seenObjective) {
      seenObjective = true;
      const name = String(ev.payload.name ?? objective?.name ?? 'the objective');
      candidates.push({
        t: ev.t, eventId: ev.id, kind: 'objective', cell: ev.cell,
        graphicId: typeof ev.payload.graphicId === 'string' ? ev.payload.graphicId : objective?.id,
        headline: `${name} seized`,
        locationLabel: name,
        extraCriteria: [`Three maneuver platoons stood on ${name} with no living enemy inside it.`],
      });
    }
  }

  candidates.sort((a, b) => a.t - b.t || a.eventId - b.eventId);

  const blueOrders = orders.filter((o) => o.side === 0).sort((a, b) => a.issuedAt - b.issuedAt || a.id - b.id);
  const armorHeld = units.filter((u) => u.side === 0 && u.type === 'armor');
  const gunners = units.filter((u) => u.side === 0 && (u.type === 'spArty' || u.type === 'mortar'));

  return candidates.slice(0, MAX_DECISION_POINTS).map((c, i) => {
    const n = i + 1;
    const windowEnd = c.t + DECISION_WINDOW_TICKS;
    const chosenOrders = blueOrders.filter((o) => o.issuedAt >= c.t && o.issuedAt <= windowEnd);

    // ---- criteria: only what was in the picture at that tick ----------------------------------
    const held = contactBreakdown(spans, c.t);
    const criteria: string[] = [...c.extraCriteria];
    criteria.push(
      held.count === 0
        ? 'No enemy contact was held at this minute: the picture was empty.'
        : `${held.count} enemy contact${held.count === 1 ? '' : 's'} held (${held.text}).`,
    );
    const recent = sorted.filter(
      (ev) => ev.side === 0 && ev.significant && ev.t <= c.t && ev.t >= c.t - CRITERIA_LOOKBACK_TICKS
        && ev.id !== c.eventId && (ev.kind === 'contact.new' || ev.kind === 'nai.contact' || ev.kind === 'unit.broken'),
    );
    if (recent.length > 0) {
      criteria.push(`${recent.length} other report${recent.length === 1 ? '' : 's'} in the preceding ten minutes.`);
    }
    criteria.push(`Lead element ${furthestPl(sorted, c.t)}.`);

    // ---- options: the plays that were on the table, then the three a commander always has ------
    const options: DecisionOption[] = [];
    const byPlay = new Map<string, Order[]>();
    for (const o of chosenOrders) {
      if (!o.playId) continue;
      const list = byPlay.get(o.playId);
      if (list) list.push(o); else byPlay.set(o.playId, [o]);
    }
    for (const [playId, list] of byPlay) {
      options.push({
        id: playId,
        label: playId.replace(/[:_-]/g, ' ').toUpperCase(),
        detail: `An advisor play of ${list.length} order${list.length === 1 ? '' : 's'}, recorded in the order log at this minute.`,
        source: 'play',
        playId,
      });
    }
    options.push({
      id: 'commit-reserve',
      label: 'Commit the reserve',
      detail: armorHeld.length > 0
        ? `Throw the armor platoons (${armorHeld.map((u) => u.callsign).join(', ')}) at the objective now.`
        : 'Throw the uncommitted maneuver platoons at the objective now.',
      source: 'template',
    });
    options.push({
      id: 'shift-fires',
      label: 'Shift fires',
      detail: gunners.length > 0
        ? `A fire mission from ${gunners.map((u) => u.callsign).join(', ')} onto this decision point.`
        : 'A fire mission onto this decision point.',
      source: 'template',
    });
    options.push({
      id: 'hold',
      label: 'Hold in place',
      detail: 'Stand down what is moving and let the picture develop before committing anything else.',
      source: 'template',
    });

    // ---- what you chose -------------------------------------------------------------------------
    const chosenSummary = chosenOrders.length === 0
      ? 'no order'
      : chosenOrders
        .map((o) => `${unitById.get(o.unitId)?.callsign ?? `UNIT ${o.unitId}`} ${o.card}`)
        .slice(0, 6)
        .join(', ') + (chosenOrders.length > 6 ? `, +${chosenOrders.length - 6} more` : '');

    // ---- outcome fifteen minutes later ----------------------------------------------------------
    const outEnd = c.t + OUTCOME_WINDOW_TICKS;
    let blueDestroyed = 0, blueBroken = 0, redDestroyed = 0, objectiveSeized = false;
    for (const ev of sorted) {
      if (ev.t <= c.t || ev.t > outEnd) continue;
      if (ev.kind === 'unit.destroyed') { if (ev.side === 0) blueDestroyed++; else redDestroyed++; }
      else if (ev.kind === 'unit.broken' && ev.side === 0) blueBroken++;
      else if (ev.kind === 'obj.seized') objectiveSeized = true;
    }
    const parts: string[] = [];
    parts.push(blueDestroyed === 0 && blueBroken === 0
      ? 'no friendly platoon lost'
      : `${blueDestroyed} friendly destroyed, ${blueBroken} broken`);
    parts.push(`${redDestroyed} enemy destroyed`);
    const plProgress = furthestPl(sorted, outEnd);
    parts.push(objectiveSeized ? 'the objective fell' : `lead element ${plProgress}`);
    const outcome: DecisionOutcome = {
      summary: `Fifteen minutes later: ${parts.join('; ')}.`,
      blueDestroyed, blueBroken, redDestroyed, plProgress, objectiveSeized,
    };

    const observerUnit = c.observerId !== undefined ? unitById.get(c.observerId) : undefined;

    return {
      id: `dp-${c.eventId}`,
      n,
      t: c.t,
      kind: c.kind,
      eventId: c.eventId,
      cell: c.cell,
      graphicId: c.graphicId,
      title: `DP ${n} — ${c.headline}`,
      locationLabel: c.locationLabel,
      criteria,
      options,
      chosen: { orders: chosenOrders, summary: chosenSummary },
      observer: observerUnit ? { unitId: observerUnit.id, callsign: observerUnit.callsign } : null,
      outcome,
    };
  });
}

/**
 * The decision points as one block of plain text, for the rapporteur's prompt (src/ui/debrief/
 * Narrative.tsx appends it to the facts). Every line names the event id it rests on, so a paragraph
 * that cites one is citing an id the prompt already listed.
 */
export function decisionPointsSummary(dps: DecisionPoint[]): string {
  if (dps.length === 0) return 'No decision points were detected in this run.';
  return dps.map((dp) => [
    `${dp.title} at H+${Math.round(dp.t / TICKS_PER_MIN)}m (event ${dp.eventId}), ${dp.locationLabel}.`,
    `Criteria: ${dp.criteria.join(' ')}`,
    `Options: ${dp.options.map((o) => o.label).join(' / ')}.`,
    `Chose: ${dp.chosen.summary}.`,
    `Observed by: ${dp.observer?.callsign ?? 'the staff'}.`,
    dp.outcome.summary,
  ].join(' ')).join('\n');
}

// ---- branchDelta ------------------------------------------------------------------------------------

/** The two runs compared, and the one line that states the difference. */
export interface BranchComparison {
  /** "+2 platoons intact, objective reached 23 min earlier". */
  line: string;
  platoonsIntactReal: number;
  platoonsIntactAlt: number;
  /** Tick the objective fell, or null if it never did. */
  objectiveTickReal: number | null;
  objectiveTickAlt: number | null;
  redDestroyedReal: number;
  redDestroyedAlt: number;
}

/** Blue maneuver platoons still alive in the last ground-truth frame of a run. */
function platoonsIntact(truth: TruthFrame[], units: Unit[]): number {
  const last = truth[truth.length - 1];
  if (!last) return 0;
  const maneuver = new Set(
    units.filter((u) => u.side === 0 && (u.type === 'armor' || u.type === 'mech' || u.type === 'inf'))
      .map((u) => u.id),
  );
  return last.units.filter((u) => u.alive && maneuver.has(u.id)).length;
}

function objectiveTick(events: Event[]): number | null {
  const ev = events.find((e) => e.kind === 'obj.seized');
  return ev ? ev.t : null;
}

function redDestroyed(events: Event[]): number {
  return events.filter((e) => e.kind === 'unit.destroyed' && e.side === 1).length;
}

/**
 * What the branch changed, in the terms the player cares about: platoons still on their feet, whether
 * and when the objective fell, and how much of the enemy went with it. Both runs share a unit list -
 * the branch is the same scenario with one decision changed - so the sides and types are read from
 * the real run's units for both.
 */
export function branchDelta(
  real: { truth: TruthFrame[]; events: Event[] },
  alt: { truth: TruthFrame[]; events: Event[] },
  units: Unit[],
): BranchComparison {
  const platoonsIntactReal = platoonsIntact(real.truth, units);
  const platoonsIntactAlt = platoonsIntact(alt.truth, units);
  const objectiveTickReal = objectiveTick(real.events);
  const objectiveTickAlt = objectiveTick(alt.events);
  const redDestroyedReal = redDestroyed(real.events);
  const redDestroyedAlt = redDestroyed(alt.events);

  const parts: string[] = [];
  const dPl = platoonsIntactAlt - platoonsIntactReal;
  parts.push(dPl === 0
    ? 'the same platoons intact'
    : `${dPl > 0 ? '+' : ''}${dPl} platoon${Math.abs(dPl) === 1 ? '' : 's'} intact`);

  if (objectiveTickAlt !== null && objectiveTickReal !== null) {
    const dMin = Math.round((objectiveTickAlt - objectiveTickReal) / TICKS_PER_MIN);
    parts.push(dMin === 0
      ? 'objective reached at the same minute'
      : `objective reached ${Math.abs(dMin)} min ${dMin < 0 ? 'earlier' : 'later'}`);
  } else if (objectiveTickAlt !== null) {
    // Floored, not rounded, so this minute reads the same as the `clockHM` beside it in the panel.
    parts.push(`objective reached at H+${Math.floor(objectiveTickAlt / TICKS_PER_MIN)} min, where the real run never took it`);
  } else if (objectiveTickReal !== null) {
    parts.push('objective not reached at all');
  } else {
    parts.push('the objective fell in neither run');
  }

  const dRed = redDestroyedAlt - redDestroyedReal;
  if (dRed !== 0) parts.push(`${dRed > 0 ? '+' : ''}${dRed} enemy destroyed`);

  return {
    line: parts.join(', '),
    platoonsIntactReal, platoonsIntactAlt,
    objectiveTickReal, objectiveTickAlt,
    redDestroyedReal, redDestroyedAlt,
  };
}
